import { Dispatch, Fragment, SetStateAction } from 'react';
import * as R from 'ramda';
import * as Dfns from 'date-fns/fp';

import { MdDeleteForever, MdArrowForward, MdFastForward } from 'react-icons/md';
import { PlanRow, RowEdit } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { IconButton } from '@Components/IconButton';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { PRETTY_EDIT_NAMES } from '../linear-buying-constants';

import {
  deepMergeLinearImpressionOverrides,
  ExtendEditModalData,
  formatLinearImpressionOverrides
} from '../linear-buying-utils';

interface PendingEditProps {
  editData: ExtendEditModalData;
  deleteEdit: (key: string, editType: string) => void;
  setExtendEditModalData: Dispatch<SetStateAction<ExtendEditModalData | undefined>>;
}

export const PendingEdit = ({ editData, deleteEdit, setExtendEditModalData }: PendingEditProps) => {
  const { row, edits } = editData;
  const { key, week } = row;

  return (
    <div className='edit-container' key={key}>
      <div className='edit-title'>
        {/* Network and line live on the headers above, so the card only has to say which week. */}
        <span>{`w/o ${Dfns.format('M/d', Dfns.parseISO(week))}`}</span>
        <span className='change-badge edited'>Edited</span>
      </div>
      <div className='edits-list'>
        {R.keys(edits).map(editType => {
          const name = PRETTY_EDIT_NAMES[editType];

          const newRotation: Record<string, number> = {};
          const oldRotation: Record<string, number> = {};
          if (editType === 'creatives') {
            for (const creativeMetadata of edits[editType]!) {
              const { isci, percent } = creativeMetadata;
              newRotation[isci] = percent;
            }
            if (row.creatives) {
              for (const creativeMetadata of row.creatives) {
                const { isci, percent } = creativeMetadata;
                oldRotation[isci] = percent;
              }
            }
          }

          return (
            <div className='edit' key={editType}>
              <div className='edit-body'>
                <span className='edit-type'>{name}</span>
                <div className='edit-diff'>
                  {editType === 'creatives' ? (
                    Object.keys(newRotation).map(isci => {
                      const pct = newRotation[isci];
                      const unchanged = newRotation[isci] === oldRotation[isci];
                      return (
                        <Fragment key={isci}>
                          <span className='isci'>{isci}</span>
                          <span className={unchanged ? 'unchanged' : 'old-value'}>
                            {oldRotation[isci] ? `${oldRotation[isci]}%` : '0%'}
                          </span>
                          <MdArrowForward className='diff-arrow' />
                          <span className={unchanged ? 'unchanged' : 'new-value'}>{`${pct}%`}</span>
                        </Fragment>
                      );
                    })
                  ) : (
                    <ScalarEditDiff editType={editType} row={row} edits={edits} />
                  )}
                </div>
              </div>
              <div className='edit-actions'>
                <IconButton
                  disabled={editType === 'impressionOverrides'}
                  size={Size.SM}
                  appearance={Appearance.EMPTY}
                  design={Design.NEUTRAL}
                  onClick={() =>
                    setExtendEditModalData({ ...editData, selectedEditToExtend: editType })
                  }
                >
                  <MdFastForward />
                </IconButton>
                <IconButton
                  size={Size.SM}
                  appearance={Appearance.EMPTY}
                  design={Intent.ERROR}
                  onClick={() => deleteEdit(key, editType)}
                >
                  <MdDeleteForever />
                </IconButton>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * `0` is a real value here — Type is a raw numeric enum where Regular is 0, and Count and Cost can
 * legitimately be zeroed — so only a nullish or empty value falls back to 'N/A'.
 */
const isBlank = (value: unknown): boolean => value === null || value === undefined || value === '';

interface ScalarEditDiffProps {
  editType: Exclude<keyof RowEdit, 'creatives'>;
  row: PlanRow;
  edits: RowEdit;
}

/** Old → new for every edit type except `creatives`, which renders as a per-ISCI rotation diff. */
const ScalarEditDiff = ({ editType, row, edits }: ScalarEditDiffProps) => {
  // For impression overrides edits, we want to merge the current overrides with the edits
  // to show the complete object. Otherwise, it will look like the edits object is replacing the
  // current impression overrides object.
  if (editType === 'impressionOverrides') {
    const merged = deepMergeLinearImpressionOverrides(row[editType] || {}, edits[editType] || {});
    return (
      <>
        <span className='old-value'>
          {formatLinearImpressionOverrides(row[editType] || {}) || 'N/A'}
        </span>
        <MdArrowForward className='diff-arrow' />
        <span className='new-value'>{formatLinearImpressionOverrides(merged)}</span>
      </>
    );
  }

  return (
    <>
      <span className='old-value'>{isBlank(row[editType]) ? 'N/A' : row[editType]}</span>
      <MdArrowForward />
      <span className='new-value'>{edits[editType]}</span>
    </>
  );
};
